import * as React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton,
  MenuItem, Paper, Popover, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import api from '../services/api';

const METHODS = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
  { value: 'teams', label: 'Teams' },
  { value: 'outro', label: 'Outro' },
];

export default function TabContatos({ props, showNotify }) {
  const [contatos, setContatos] = React.useState([]);
  const [empresaInfo, setEmpresaInfo] = React.useState(null);
  const [escalations, setEscalations] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [escalLoading, setEscalLoading] = React.useState(false);

  // Popover state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedContato, setSelectedContato] = React.useState(null);
  const [escalMethod, setEscalMethod] = React.useState('telefone');
  const [escalObs, setEscalObs] = React.useState('');

  React.useEffect(() => {
    if (!props.codEmpresa) return;
    setLoading(true);

    Promise.all([
      api.get(`empresa-athena/contatos/${props.codEmpresa}`).then(r => r.data).catch(() => []),
      api.get(`empresa-athena/info/${props.codEmpresa}`).then(r => r.data).catch(() => null),
      !Array.isArray(props.eventId)
        ? api.get(`escalation/find/${props.eventId}/${props.codEmpresa}`).then(r => r.data).catch(() => [])
        : Promise.resolve([]),
    ]).then(([c, info, esc]) => {
      setContatos(c);
      setEmpresaInfo(info);
      setEscalations(esc);
    }).finally(() => setLoading(false));
  }, [props.codEmpresa, props.eventId]);

  const handleAcionei = (event, contato) => {
    setAnchorEl(event.currentTarget);
    setSelectedContato(contato);
    setEscalMethod('telefone');
    setEscalObs('');
  };

  const handleSendEscalation = () => {
    if (!selectedContato) return;
    setEscalLoading(true);
    api.post('escalation/create/', {
      event_id: props.eventId,
      cod_empresa: props.codEmpresa,
      contato_id: selectedContato.id,
      contato_nome: selectedContato.nome,
      method: escalMethod,
      observation: escalObs || null,
      source: props.source || 'zabbix',
    })
      .then(res => {
        setEscalations(prev => [{
          id: res.data.id,
          contato_nome: selectedContato.nome,
          method: escalMethod,
          observation: escalObs,
          created_at: new Date().toISOString(),
        }, ...prev]);
        setAnchorEl(null);
        if (showNotify) showNotify('Acionamento registrado!', 'success');
      })
      .catch(() => {
        if (showNotify) showNotify('Erro ao registrar acionamento', 'error');
      })
      .finally(() => setEscalLoading(false));
  };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      {/* Info empresa */}
      {empresaInfo && (
        <>
          {/* Links/Circuitos */}
          {empresaInfo.links?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>Links / Circuitos</Typography>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Operadora</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Circuito</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Obs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {empresaInfo.links.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ py: 0.5 }}>{l.tipo_link_nome || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{l.operadora_nome || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{l.numero_circuito || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{l.observacoes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Servicos + ASNs + Blocos */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {empresaInfo.servicos?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" fontWeight={600}>Servicos</Typography>
                {empresaInfo.servicos.map((s, i) => (
                  <Chip key={i} label={s.servico_descricao} size="small" sx={{ m: 0.25 }} />
                ))}
              </Paper>
            )}
            {empresaInfo.asns?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, flex: 1, minWidth: 150 }}>
                <Typography variant="caption" fontWeight={600}>ASNs</Typography>
                {empresaInfo.asns.map((a, i) => (
                  <Chip key={i} label={`AS${a.asn_number}`} size="small" sx={{ m: 0.25 }} />
                ))}
              </Paper>
            )}
            {empresaInfo.blocos_ip?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" fontWeight={600}>Blocos IP</Typography>
                {empresaInfo.blocos_ip.map((b, i) => (
                  <Chip key={i} label={b.bloco_cidr} size="small" sx={{ m: 0.25 }} title={b.observacoes || ''} />
                ))}
              </Paper>
            )}
          </Box>
        </>
      )}

      {/* Contatos */}
      {contatos.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>Contatos</Typography>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Cargo</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Telefones</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Expediente</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="center">Acao</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contatos.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ py: 0.5 }}>{c.nome}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{c.cargo || '—'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{c.tipo_contato_nome || '—'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {c.telefones?.map((t, i) => (
                        <Chip
                          key={i}
                          icon={t.tipo === 'whatsapp' ? <WhatsAppIcon fontSize="small" /> : <PhoneIcon fontSize="small" />}
                          label={`${t.telefone}${t.tipo ? ` (${t.tipo})` : ''}`}
                          size="small" variant="outlined"
                          sx={{ m: 0.25, fontSize: '0.7rem' }}
                        />
                      )) || '—'}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                      {c.expedientes?.map((e, i) => {
                        const dias = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];
                        return (
                          <span key={i}>
                            {dias[e.dia_semana]} {e.hora_inicio}-{e.hora_fim}
                            {i < c.expedientes.length - 1 && ', '}
                          </span>
                        );
                      }) || '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      {!Array.isArray(props.eventId) && (
                        <Button
                          size="small" variant="outlined" color="warning"
                          startIcon={<NotificationsActiveIcon />}
                          onClick={(e) => handleAcionei(e, c)}
                          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                        >
                          Acionei
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 1.5 }}>Nenhum contato encontrado no Athena para esta empresa.</Alert>
      )}

      {/* Popover Acionamento */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Registrar acionamento: {selectedContato?.nome}
          </Typography>
          <Select
            fullWidth size="small" value={escalMethod}
            onChange={(e) => setEscalMethod(e.target.value)}
            sx={{ mb: 1 }}
          >
            {METHODS.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
          <TextField
            fullWidth size="small" label="Observacao" multiline rows={2}
            value={escalObs} onChange={(e) => setEscalObs(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setAnchorEl(null)}>Cancelar</Button>
            <Button
              size="small" variant="contained" color="warning"
              onClick={handleSendEscalation} disabled={escalLoading}
            >
              {escalLoading ? <CircularProgress size={16} /> : 'Registrar'}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Historico acionamentos */}
      {escalations.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>
            <HistoryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Historico de Acionamentos
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {escalations.map((esc, i) => (
              <Alert key={esc.id || i} severity="info" sx={{ mb: 0.5, py: 0 }} icon={false}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(esc.created_at).toLocaleString('pt-BR')} - {esc.user_name || ''}
                </Typography>
                <Typography variant="body2">
                  <b>{esc.contato_nome}</b> via <Chip label={esc.method} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Typography>
                {esc.observation && <Typography variant="caption">{esc.observation}</Typography>}
              </Alert>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
